import { last, find, pick, difference, max, get, flatten } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'

import { useAppStore, useTbViewStore, useAppEx } from '../index'

const hanNum = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十']

const strifyArea = (num, a = []) => {
  const area = last(a) || {}
  return `area_${area.id || area.objectId}_${num}`
}

function useMatrix() {
  const { userInfoInApp = {} } = useAppEx((state) =>
    pick(state, ['userInfoInApp'])
  )
  const {
    matrixSelected, // 勾选指标或点位ids
    matrixConfig = {},
    standardConfig,
    mergeStandardIds,
    setMatrixInited,
    setMatrix,
  } = useTbViewStore((state) =>
    pick(state, [
      'matrixConfig',
      'setMatrix',
      'matrixSelected',
      'matrixConfig',
      'standardConfig',
      'mergeStandardIds',
      'setMatrixInited',
    ])
  )

  const [matrixMaterialData, setMatrixMaterialData] = useState(null)
  const { standardStyles } = matrixConfig
  const headerSlugs = useMemo(() => {
    const { headerSlug = [] } = matrixConfig
    return difference(headerSlug, ['area'])
  }, [matrixConfig])

  // 用于指标筛选
  const { filterScope: standardId } = standardConfig || {}

  const getDitto = useAppStore((x) => x.getDitto)

  const standardModel = getDitto('standard')
  const areaModel = getDitto('area')

  // 准备点位、指标数据
  const initMatrix = () => {
    const hiddenStandardsIds = flatten(
      (mergeStandardIds || []).map((v) => v.hiddenStandards)
    )
    setMatrixInited(false)
    Promise.all([
      areaModel.fetchAllEffectiveData(),
      standardModel.getTreeTable(standardId, {
        filterStandardIds: hiddenStandardsIds,
      }),
    ]).then((result) => {
      setMatrixMaterialData({ areas: result[0], standards: result[1] })
      setMatrixInited(true)
    })
  }

  const filterStandards = useMemo(() => {
    const arr = get(matrixMaterialData, 'standards', []).filter((stds) => {
      if (
        !(
          matrixConfig.matrixSelected !== 'area' &&
          matrixSelected &&
          matrixSelected.length > 0
        )
      ) {
        return true
      }
      const std = last(stds) // 末级肯定会被勾上，所以只判断指标末级就可以
      return matrixSelected.includes(std.objectId)
    })
    return arr
  }, [matrixMaterialData, matrixConfig, matrixSelected, mergeStandardIds])

  const fetchMatrixTbData = (
    tbData,
    fields,
    matrixCustomFields,
    customColumnsTitle,
    areas,
    standards,
    _mergeStandardIds
  ) => {
    const queries =
      matrixConfig.matrixSelected === 'area'
        ? {
            area: matrixSelected,
          }
        : {
            standard: matrixSelected,
          }

    let standardLength = 0
    // 点位相关
    // 默认全部点位
    let finalShowAreas = Object.values(areas)
    // 是否筛选了点位
    const isUseAreaQueries = queries.area && queries.area.length > 0
    if (isUseAreaQueries) {
      finalShowAreas = finalShowAreas.filter((area) =>
        (queries.area || []).includes(area.objectId)
      )
    }

    const maxStandardsLevel = max(standards.map((v) => v.length)) - 1

    const showTbDataGroupByArea = finalShowAreas.map((area) => {
      return {
        area,
        tbData: tbData.filter((v) => {
          const ids = (v.area || []).map((a) => a.objectId)
          return last(ids) === area.objectId
        }),
      }
    })

    // 构建最终数据
    // step-1 构建头部行数据(视图配置的非指标绑定的字段)
    const headerLines = headerSlugs.map((slug) => {
      const field = fields.find((v) => v.slug === slug)
      const transedLine = {
        objectId: slug,
        [`standard_${maxStandardsLevel}`]: field && field.title,
        bind_raw_slug: slug, // 为了回显数据
      }
      tbData.forEach((tbLine) => {
        const areaStr = strifyArea(null, tbLine.area)

        if (areaStr) {
          transedLine[areaStr] = tbLine[slug]
        }
      })
      // 确定一下前后处理是否重复
      showTbDataGroupByArea.forEach((item) => {
        const { tbData: showData } = item
        showData.forEach((data, index) => {
          const areaStr = strifyArea(index, data.area)

          if (areaStr) {
            transedLine[areaStr] = data[slug]
          }
        })
      })

      return transedLine
    })

    // step-2 构建指标行数据
    const transedLines = standards.map((stdLine) => {
      const stdLast = last(stdLine)
      const mergeStandardInfo = (_mergeStandardIds || []).find(
        (v) => v.mergeValueTo === stdLast.id
      )

      // 开始构造最终数据的单行数据
      const transedLine = {
        objectId: stdLast.id, // 只有末级节点唯一，故采用末级节点id作为该条数据的objectId
      }

      standardLength =
        stdLine.length > standardLength ? stdLine.length : standardLength

      if (mergeStandardInfo) {
        const { hiddenStandards } = mergeStandardInfo
        const values = [`bind_standard_${stdLast.id}`]
        hiddenStandards.forEach((f) => {
          values.push(`bind_standard_${f}`)
        })
        transedLine.bind_merge_raw_slugs = values
      }
      // 组装指标列数据
      stdLine.forEach((st, i) => {
        transedLine[`standard_${i}`] = st
      })

      // 组装统计列数据
      transedLine.bind_raw_slug = `bind_standard_${stdLast.id}`

      // 组装点位列数据
      showTbDataGroupByArea.forEach((item) => {
        const { tbData: showData } = item
        showData.forEach((tbLine, index) => {
          const areaStr = strifyArea(index, tbLine.area)

          if (areaStr) {
            // 合并指标
            if (mergeStandardInfo) {
              const { hiddenStandards } = mergeStandardInfo
              const values = [
                tbLine[`bind_standard_${stdLast.id}`] || transedLine[areaStr],
              ]
              hiddenStandards.forEach((f) => {
                values.push(tbLine[`bind_standard_${f}`])
              })
              transedLine[areaStr] = values
            } else {
              transedLine[areaStr] =
                tbLine[`bind_standard_${stdLast.id}`] || transedLine[areaStr]
            }
            const obj = fields.find(
              (field) =>
                field.slug === `bind_standard_${stdLast.id}` &&
                (field.type === 'dropSelect' || field.type === 'select')
            )
            // 定制操作列
            if (matrixCustomFields && matrixCustomFields.length > 0) {
              matrixCustomFields.forEach((cField) => {
                transedLine[cField.slug] = obj
              })
            }
          }
        })
      })

      return transedLine
    })

    // 构造合并单元格数据
    const mergeCellInfoNow = []

    for (let li = 0; li < transedLines.length; li++) {
      const line = transedLines[li]
      const mc = {
        objectId: line.objectId,
        allObjectIds: [line.objectId],
        merge: [],
      }

      for (let si = 0; si < standardLength; si++) {
        const slug = `standard_${si}`
        if (line[slug]) {
          if (li > 0) {
            let i = 1

            while (li >= i) {
              if (!transedLines[li - i][slug]) {
                break
              }
              if (transedLines[li - i][slug].id === line[slug].id) {
                i++
              } else {
                break
              }
            }

            const lastLine = transedLines[li - i + 1]
            const lastMc = mergeCellInfoNow[li - i + 1]

            if (
              lastLine &&
              lastMc &&
              lastLine[slug] &&
              lastLine[slug].id === line[slug].id
            ) {
              // eslint-disable-next-line no-constant-condition
              const merge = find(lastMc.merge, { slug })
              if (merge) {
                merge.rowSpan += 1
              }
              lastMc.allObjectIds.push(line.objectId)
            } else {
              mc.merge.push({
                slug,
                rowSpan: 1,
                colSpan: 1,
              })
            }
          } else {
            mc.merge.push({
              slug,
              rowSpan: 1,
              colSpan: 1,
            })
          }
        } else {
          last(mc.merge).colSpan += 1
        }
      }
      mergeCellInfoNow.push(mc)
    }

    const newColumns = [
      {
        group: 'system',
        nodeTemplateId: 0,
        slug: 'tableDataId',
        title: '编号',
        hidden: true,
        type: 'identifier',
      },
    ]

    hanNum.slice(0, standardLength).forEach((han, i) => {
      let title = `${han}级指标`
      if (customColumnsTitle && customColumnsTitle[han]) {
        title = customColumnsTitle[han]
      } else if (customColumnsTitle && customColumnsTitle.others) {
        title = customColumnsTitle.others
      }

      newColumns.push({
        title,
        type: 'standard',
        slug: `standard_${i}`,
        'inline-nowrap': false,
        showType: 'sticky',
        style: standardStyles && standardStyles[han], // 远洋（视图配置就行）
        bind_primary: true,
      })
    })

    if (matrixCustomFields && matrixCustomFields.length > 0) {
      matrixCustomFields.forEach((item) => {
        newColumns.push(item)
      })
    }

    // 构造点位列（同个点位可能有多列）
    showTbDataGroupByArea.forEach((item) => {
      const { area, tbData: showData } = item
      showData.forEach((data, index) => {
        return newColumns.push({
          title: area.title,
          type: 'text',
          slug: `${strifyArea(index, [area])}`,
          operators: {
            allow: true,
            payload: {
              allowEditor: (area.bindUsers || []).includes(userInfoInApp.id),
              objectId: data.objectId,
              area: [area.objectId],
              rawRowData: data,
            },
          },
        })
      })
    })

    setMatrix(
      [...headerLines, ...transedLines],
      newColumns,
      mergeCellInfoNow,
      newColumns
        .filter((v) => !v.hidden)
        .map((v) => {
          if (!v.showType) {
            return { ...v, showType: 'show' }
          }
          return v
        }),
      standardLength
    )

    return null
  }

  useEffect(() => {
    initMatrix()
  }, [])

  return {
    fetchMatrixTbData,
    standardModel,
    areaModel,
    matrixMaterialData,
    filterStandards,
  }
}

export default useMatrix
